/**
 * Device templates: the default port list per device model
 * (not to be confused with studio templates, which are configs).
 *
 * Creating a device from a template creates its ports. Templates of devices
 * with built-in control also give each port's control address. Live
 * discovery ({@link reconcileReportedPorts}) fills in what a template doesn't
 * know and flags ports the device no longer reports; it never deletes.
 */
export const ATEM_AUX_GROUP = "Aux";
export const ATEM_MULTIVIEWER_GROUP = "MV";
function range(count) {
    return Array.from({ length: count }, (_, index) => index + 1);
}
function smartVideohub(size) {
    return [
        ...range(size).map((number) => ({
            direction: "input",
            signal: "sdi",
            number,
            name: null,
            group: null,
            control: { kind: "videohub_input", index: number - 1 },
        })),
        ...range(size).map((number) => ({
            direction: "output",
            signal: "sdi",
            number,
            name: null,
            group: null,
            control: { kind: "videohub_output", index: number - 1 },
        })),
    ];
}
export const DEVICE_TEMPLATES = [
    {
        id: "blackmagic-smart-videohub-40x40",
        label: "Smart Videohub 40×40",
        model: "Blackmagic Smart Videohub 40x40 12G",
        kind: "hardware",
        connectionType: "videohub",
        ports: smartVideohub(40),
    },
    {
        id: "blackmagic-atem-constellation-8k",
        label: "ATEM Constellation 8K",
        model: "Blackmagic ATEM Constellation 8K",
        kind: "hardware",
        connectionType: "atem",
        // Program audio on 1+2 of every output, the rest is silent
        embeddedAudio: "1-2",
        ports: [
            ...range(40).map((number) => ({
                direction: "input",
                signal: "sdi",
                number,
                name: null,
                group: null,
                // ATEM input IDs of the SDI inputs are their 1-based numbers
                control: { kind: "atem_input", input_id: number },
            })),
            ...range(24).map((number) => ({
                direction: "output",
                signal: "sdi",
                number,
                name: null,
                group: ATEM_AUX_GROUP,
                control: { kind: "atem_aux", index: number - 1 },
            })),
            ...range(4).map((number) => ({
                direction: "output",
                signal: "sdi",
                number,
                name: null,
                group: ATEM_MULTIVIEWER_GROUP,
                control: { kind: "atem_multiviewer", index: number - 1 },
            })),
        ],
    },
    {
        id: "blackmagic-decklink-quad-2",
        label: "DeckLink Quad 2",
        model: "Blackmagic DeckLink Quad 2",
        kind: "hardware",
        connectionType: "none",
        captureCard: { vmixName: "DeckLink Quad" },
        // Half-duplex SDI: each connector is an input or an output, set in
        // Desktop Video, so the ports are bidirectional.
        ports: range(8).map((number) => ({
            direction: "bidirectional",
            signal: "sdi",
            number,
            name: null,
            group: null,
            control: null,
        })),
    },
];
export function getDeviceTemplate(templateID) {
    if (!templateID)
        return undefined;
    return DEVICE_TEMPLATES.find((template) => template.id === templateID);
}
/** Template ID recorded for devices built with {@link buildGenericPorts}. */
export const GENERIC_DEVICE_TEMPLATE_ID = "generic";
/** Generic device: "N inputs / M outputs of signal X". */
export function buildGenericPorts({ inputs, outputs, signal, bidirectional = 0, }) {
    const make = (direction) => (number) => ({
        direction,
        signal,
        number,
        name: null,
        group: null,
        control: null,
    });
    return [
        ...range(Math.max(0, inputs)).map(make("input")),
        ...range(Math.max(0, outputs)).map(make("output")),
        ...range(Math.max(0, bidirectional)).map(make("bidirectional")),
    ];
}
/** Uniqueness key of a port on its device (matches the PG unique index). */
export function portSlotKey(port) {
    return `${port.direction}|${port.group ?? ""}|${port.number}`;
}
/**
 * The template ports a device does not have yet (by direction, group and
 * number), so "create ports from template" can run again without duplicates.
 */
export function missingTemplatePorts(template, existing) {
    const taken = new Set(existing.map((port) => portSlotKey({
        direction: port.direction,
        group: port.group ?? null,
        number: port.number,
    })));
    return template.filter((port) => !taken.has(portSlotKey(port)));
}
/**
 * Translate a capture card port into the addresses software on the PC uses.
 *
 * Cards are the capture-card devices (template with `captureCard`) under the
 * same parent PC, ordered by `position` (then name). The port's
 * `device_index` is its position across all of their ports, 0-based:
 * on a PC with one DeckLink Quad 2, DL7 → 6. vMix numbers devices per model
 * name, 1-based: DL7 → "DeckLink Quad (7)".
 *
 * Returns null for ports that are not on a capture card inside a PC.
 */
export function captureCardPortAddress(portID, devices, ports) {
    const port = ports.find((p) => p.id === portID);
    if (!port)
        return null;
    const card = devices.find((d) => d.id === port.deviceID);
    const cardTemplate = getDeviceTemplate(card?.templateID);
    if (!card || !card.parentID || !cardTemplate?.captureCard)
        return null;
    const cards = devices
        .filter((d) => d.parentID === card.parentID &&
        getDeviceTemplate(d.templateID)?.captureCard)
        .sort((a, b) => {
        const pa = a.position ?? Number.MAX_SAFE_INTEGER;
        const pb = b.position ?? Number.MAX_SAFE_INTEGER;
        if (pa !== pb)
            return pa - pb;
        return a.name.localeCompare(b.name) || a.id.localeCompare(b.id);
    });
    const portsOf = (deviceID) => ports
        .filter((p) => p.deviceID === deviceID)
        .sort((a, b) => a.number - b.number || a.id.localeCompare(b.id));
    let deviceIndex = 0;
    let vmixIndex = 0;
    for (const current of cards) {
        const currentVmix = getDeviceTemplate(current.templateID)?.captureCard
            ?.vmixName;
        const currentPorts = portsOf(current.id);
        if (current.id === card.id) {
            const offset = currentPorts.findIndex((p) => p.id === port.id);
            if (offset < 0)
                return null;
            return {
                deviceIndex: deviceIndex + offset,
                vmixName: `${cardTemplate.captureCard.vmixName} (${vmixIndex + offset + 1})`,
            };
        }
        deviceIndex += currentPorts.length;
        if (currentVmix === cardTemplate.captureCard.vmixName) {
            vmixIndex += currentPorts.length;
        }
    }
    return null;
}
// ─── Live discovery ─────────────────────────────────────────
/**
 * Turn a port the bridge reports for a running device (`getDevicePorts`) into
 * a stored port. Internal sources (ATEM colour bars, media players, M/E
 * programs) are not physical connectors and map to null.
 */
export function reportedPortToTemplate(port) {
    switch (port.port_type) {
        case "videohub_input":
            return {
                direction: "input",
                signal: "sdi",
                number: port.index + 1,
                name: null,
                group: null,
                control: { kind: "videohub_input", index: port.index },
            };
        case "videohub_output":
            return {
                direction: "output",
                signal: "sdi",
                number: port.index + 1,
                name: null,
                group: null,
                control: { kind: "videohub_output", index: port.index },
            };
        case "atem_input":
            // Physical inputs have IDs 1..n; 1000+ are internal sources
            if (port.index < 1 || port.index >= 1000)
                return null;
            return {
                direction: "input",
                signal: "sdi",
                number: port.index,
                name: null,
                group: null,
                control: { kind: "atem_input", input_id: port.index },
            };
        case "atem_aux":
            return {
                direction: "output",
                signal: "sdi",
                number: port.index + 1,
                name: null,
                group: ATEM_AUX_GROUP,
                control: { kind: "atem_aux", index: port.index },
            };
        default:
            return null;
    }
}
function controlKey(control) {
    if (!control)
        return null;
    if (control.kind === "atem_input")
        return `atem_input:${control.input_id}`;
    return `${control.kind}:${control.index}`;
}
/**
 * Compare a device's stored ports with what the running device reports.
 * Ports are matched by control address. Only control kinds the device reports
 * at all can be flagged missing, so an offline device (reporting nothing)
 * flags nothing. Nothing is ever deleted: callers add `toAdd` and show
 * `missing`.
 */
export function reconcileReportedPorts(stored, reported) {
    const reportedTemplates = reported
        .map(reportedPortToTemplate)
        .filter((port) => port !== null);
    const reportedKeys = new Set(reportedTemplates.map((port) => controlKey(port.control)));
    const reportedKinds = new Set(reportedTemplates.map((port) => port.control?.kind));
    const storedKeys = new Set(stored.map((port) => controlKey(port.control)));
    const storedSlots = new Set(stored.map((port) => portSlotKey({
        direction: port.direction,
        group: port.group ?? null,
        number: port.number,
    })));
    const toAdd = reportedTemplates.filter((port) => !storedKeys.has(controlKey(port.control)) &&
        // A documented port without a control address already holds the slot
        !storedSlots.has(portSlotKey(port)));
    const missing = stored.filter((port) => port.control &&
        reportedKinds.has(port.control.kind) &&
        !reportedKeys.has(controlKey(port.control)));
    return { toAdd, missing };
}
